import React, { useState, useEffect, useMemo, useContext } from 'react';
import {
    Card, CardContent, Typography, TextField, Button, Grid,
    Snackbar, Alert, IconButton, Autocomplete,
    Pagination, Stack, MenuItem, Select, FormControl, InputLabel, Box, Dialog, DialogActions,
    DialogContent, DialogTitle, Tabs, Tab, Badge,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import { validateBr } from 'js-brasil';
import { maskCPF2 } from '../../utils/mask';
import EventContext from '../../contexts/event.context';
import { format, subYears, parseISO } from 'date-fns';
import api from '../../services/api';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';
import * as qs from 'qs';
import { saveAs } from 'file-saver';
import { Table as VirtualizedTable, Column, AutoSizer } from 'react-virtualized';
import 'react-virtualized/styles.css'; // Importante para o layout


// Componente para exibir mensagens de feedback
const FeedbackMessage = ({ open, message, severity, onClose }) => (
    <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={onClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
        <Alert onClose={onClose} severity={severity} sx={{ width: '100%' }}>
            {message}
        </Alert>
    </Snackbar>
);

// Componente para o seletor de formulário
const FormSelector = ({ formName, formOptions, setFormName, setFormId, handleCreateForm, handleUpdateForm, handleToggleFormStatus,
    totalVagas, setTotalVagas, events, eventoSelecionado, setEventoSelecionado, setRoomTypes, initialRoomTypes }) => (

    <Grid container spacing={2} alignItems="center" sx={{ my: 1 }}>
        <Grid item xs={8} sm={4} md={3}>
            <Autocomplete
                freeSolo
                options={formOptions}
                getOptionLabel={(option) => option?.attributes?.name || option || ''}
                value={formOptions.find(opt => opt.attributes?.name === formName) || formName || ''}
                onChange={(event, newValue) => {
                    const normalizeEventId = (eventValue) => {
                        if (!eventValue) return '';
                        if (typeof eventValue === 'number' || typeof eventValue === 'string') {
                            return eventValue;
                        }
                        if (eventValue?.data?.id) {
                            return eventValue.data.id;
                        }
                        if (eventValue?.id) {
                            return eventValue.id;
                        }
                        return '';
                    };

                    if (typeof newValue === 'object') {
                        setFormName(newValue.attributes.name);
                        setFormId(newValue.id);
                        setTotalVagas(newValue.attributes.count_guests || '');
                        setEventoSelecionado(normalizeEventId(newValue.attributes.event));
                        // Carrega configuração de quartos do formulário selecionado
                        setRoomTypes(newValue.attributes.room_config || initialRoomTypes);
                    } else {
                        setFormName(newValue);
                        setTotalVagas('');
                        setEventoSelecionado('');
                        // Reseta configuração de quartos
                        setRoomTypes(initialRoomTypes);
                    }
                }}
                onInputChange={(event, newInputValue) => setFormName(newInputValue)}
                renderInput={(params) => (
                    <TextField {...params} label="Nome do Formulário" variant="outlined" />
                )}
            />
        </Grid>
        <Grid item sm={3}>
            <FormControl fullWidth>
                <InputLabel id="evento-label">Evento</InputLabel>
                <Select
                    labelId="evento-label"
                    value={eventoSelecionado}
                    label="Evento"
                    onChange={e => setEventoSelecionado(e.target.value)}
                >
                    <MenuItem value="">
                        <em>Selecione</em>
                    </MenuItem>
                    {events && events.map(ev => (
                        <MenuItem key={ev.id} value={ev.id}>
                            {ev.attributes?.name}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Grid>
        <Grid item>
            <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleCreateForm}
                disabled={!formName}
            >
                Criar
            </Button>
        </Grid>
        <Grid item>
            <Button
                variant="contained"
                color="warning"
                onClick={handleUpdateForm}
                disabled={!formName || !totalVagas}
            >
                Atualizar
            </Button>
        </Grid>
        <Grid item>
            <Button
                variant="contained"
                color="error"
                onClick={handleToggleFormStatus}
                disabled={!formName}
            >
                Desativar
            </Button>
        </Grid>
    </Grid>
);

// Componente para a tabela de hóspedes
// GuestsTable virtualizado
const GuestsTable = React.memo(({
    guests,
    columns,
    handleEditTable,
    handleCpfChange,
    handleSave,
    handleDelete
}) => {
    // Define larguras personalizadas para cada coluna
    const getColumnWidth = (key, totalWidth) => {
        const widths = {
            'document': 140,     // CPF
            'name': 180,         // Nome (reduzido)
            'gender': 100,       // Gênero
            'whatsapp': 140,     // WhatsApp
            'email': 180,        // Email
            'birth': 140,        // Nascimento
            'checkin': 140,      // Check-in
            'checkout': 140,     // Check-out
            'roomGroup': 140     // Grupo de Quarto
        };
        return widths[key] || Math.floor((totalWidth - 1200) / columns.length);
    };

    return (
        <Box sx={{ my: 0, border: 1, borderColor: '#eee', borderRadius: 1, height: 600 }}>
            <AutoSizer>
                {({ height, width }) => (
                    <VirtualizedTable
                        width={width}
                        height={height}
                        headerHeight={58}
                        rowHeight={80}
                        rowCount={guests.length}
                        rowGetter={({ index }) => guests[index]}
                        rowClassName={({ index }) =>
                            index % 2 === 0 ? 'evenRow' : 'oddRow'
                        }
                    >
                        {columns.map(col => (
                            <Column
                                key={col.key}
                                label={col.label}
                                dataKey={col.key}
                                width={getColumnWidth(col.key, width)}
                                cellRenderer={({ rowIndex, cellData }) =>
                                    renderTableCell(
                                        col,
                                        guests[rowIndex],
                                        guests[rowIndex].id || guests[rowIndex].tempId,
                                        handleEditTable,
                                        handleCpfChange
                                    )
                                }
                            />
                        ))}
                        <Column
                            label="Ações"
                            dataKey="actions"
                            width={120}
                            cellRenderer={({ rowIndex }) => (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <IconButton color="success" onClick={() => handleSave(guests[rowIndex].id || guests[rowIndex].tempId)}>
                                        <SaveIcon />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => handleDelete(guests[rowIndex].id || guests[rowIndex].tempId)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}
                        />
                    </VirtualizedTable>
                )}
            </AutoSizer>
        </Box>
    );
});

// Função auxiliar para renderizar células da tabela
const renderTableCell = (col, guest, idOrTempId, handleEditTable, handleCpfChange) => {
    if (col.key === 'birth') {
        return (
            <TextField
                xs={12}
                fullWidth
                key={`birth-${idOrTempId}`}
                label={col.label}
                variant='outlined'
                type="date"
                sx={{ my: 1 }}
                value={guest[col.key] || ''}
                onChange={e => handleEditTable(idOrTempId, col.key, e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                    min: format(subYears(new Date(), 100), "yyyy-MM-dd"),
                    max: format(subYears(new Date(), 17), "yyyy-MM-dd"),
                }}
            />
        );
    }

    if (col.key === 'document') {
        return (
            <TextField
                fullWidth
                xs={12}
                label={col.label}
                sx={{ my: 1 }}
                key={`document-${idOrTempId}`}
                variant='outlined'
                value={guest[col.key]}
                onChange={e => {
                    const maskedCpf = maskCPF2(e.target.value);
                    handleEditTable(idOrTempId, col.key, maskedCpf);
                    if (maskedCpf.length === 14 && validateBr.cpf(maskedCpf)) {
                        handleCpfChange(idOrTempId, maskedCpf);
                    }
                }}
                size="small"
            />
        );
    }
    if (col.key === 'checkin' || col.key === 'checkout') {
        return (
            <TextField
                fullWidth
                key={`${col.key}-${idOrTempId}`}
                label={col.label}
                xs={12}
                variant='outlined'
                sx={{ my: 1 }}
                type="date"
                value={guest[col.key] || ''}
                onChange={e => handleEditTable(idOrTempId, col.key, e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{
                    min: format(new Date(), "yyyy-MM-dd"), // Impede datas passadas
                }}
            />
        );
    }
    if (col.key === 'gender') {
        return (
            <FormControl fullWidth size="small" xs={12} sx={{ my: 1 }} key={`gender-${idOrTempId}`}>
                <InputLabel>Gênero</InputLabel>
                <Select
                    value={guest[col.key] || ''}
                    label="Gênero"
                    onChange={e => handleEditTable(idOrTempId, col.key, e.target.value)}
                >
                    <MenuItem value="M">Masculino</MenuItem>
                    <MenuItem value="F">Feminino</MenuItem>
                    <MenuItem value="Outro">Outro</MenuItem>
                </Select>
            </FormControl>
        );
    }

    return (
        <TextField
            fullWidth
            xs={12}
            sx={{ my: 1 }}
            key={`${col.key}-${idOrTempId}`}
            label={col.label}
            variant='outlined'
            value={guest[col.key] || ''}
            onChange={e => handleEditTable(idOrTempId, col.key, e.target.value)}
            size="small"
        />
    );
};

const GuestsPage = () => {
    // Configurações iniciais
    const columns = [
        { label: 'CPF', key: 'document' },
        { label: 'Nome', key: 'name' },
        { label: 'Gênero', key: 'gender' },
        { label: 'Whatsapp', key: 'whatsapp' },
        { label: 'Email', key: 'email' },
        { label: 'Nascimento', key: 'birth' },
        { label: 'Check-in', key: 'checkin' },
        { label: 'Check-out', key: 'checkout' },
        { label: 'Grupo de Quarto', key: 'roomGroup' },
    ];

    const initialRoomTypes = [
        { label: 'Single', value: 1, rooms: 0 },
        { label: 'Duplo', value: 2, rooms: 0 },
        { label: 'Triplo', value: 3, rooms: 0 },
        { label: 'Quadruplo', value: 4, rooms: 0 },
        { label: 'Quintuplo', value: 5, rooms: 0 }
    ];

    // Estados
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formName, setFormName] = useState('');
    const [formOptions, setFormOptions] = useState([]);
    const [formId, setFormId] = useState(null);
    const [guests, setGuests] = useState([]);
    const [page, setPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const [totalVagas, setTotalVagas] = useState('');
    const [totalHospedes, setTotalHospedes] = useState(null);
    const [checkin, setCheckin] = useState(format(new Date(), "yyyy-MM-dd"));
    const [checkout, setCheckout] = useState(format(new Date(), "yyyy-MM-dd"));
    const pageSize = 500;
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteGroup, setDeleteGroup] = useState(null);
    const [roomTypes, setRoomTypes] = useState(initialRoomTypes);
    const [selectedTab, setSelectedTab] = useState(initialRoomTypes[0].label);
    const [badgeCountByType, setBadgeCountByType] = useState({});
    const { events } = useContext(EventContext);
    const [eventoSelecionado, setEventoSelecionado] = useState('');
    const isMobile = useMediaQuery('(max-width:1024px)');
    const [searchName, setSearchName] = useState('');

    const toInputDate = (value) => {
        if (!value) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
        try {
            return format(parseISO(value), 'yyyy-MM-dd');
        } catch (error) {
            return '';
        }
    };

    const normalizeEventId = (eventValue) => {
        if (!eventValue) return '';
        if (typeof eventValue === 'number' || typeof eventValue === 'string') {
            return String(eventValue).trim();
        }
        if (eventValue?.data?.id) {
            return String(eventValue.data.id).trim();
        }
        if (eventValue?.id) {
            return String(eventValue.id).trim();
        }
        return '';
    };

    const applyDatesFromLastGuest = (guestList = []) => {
        if (!Array.isArray(guestList) || guestList.length === 0) return;

        // Usa o ultimo registro que possua data preenchida como padrao do formulario.
        let lastWithDates = null;
        for (let i = guestList.length - 1; i >= 0; i -= 1) {
            const guest = guestList[i];
            if (guest?.checkin || guest?.checkout) {
                lastWithDates = guest;
                break;
            }
        }

        if (!lastWithDates) return;

        const nextCheckin = toInputDate(lastWithDates.checkin);
        const nextCheckout = toInputDate(lastWithDates.checkout);

        if (nextCheckin) setCheckin(nextCheckin);
        if (nextCheckout) setCheckout(nextCheckout);
    };
    // Calcula o total de leitos disponíveis com base na configuração de quartos
    const totalLeitosDisponiveis = useMemo(() => {
        return roomTypes.reduce((total, type) => {
            return total + (type.rooms * type.value);
        }, 0);
    }, [roomTypes]);

    // Calcula as vagas restantes
    const vagasRestantes = useMemo(() => {
        return Math.max(totalLeitosDisponiveis - (totalHospedes || 0), 0);
    }, [totalLeitosDisponiveis, totalHospedes]);

    // Buscar formulários disponíveis
    const fetchForms = async () => {
        try {
            const query = qs.stringify({
                filters: {
                    enabled: {
                        $ne: false // Filtra formulários onde enabled não é false (true ou null)
                    }
                }
            });
            
            const { data, status } = await api.get(`/grids?${query}`);
            if (status === 200) {
                setFormOptions(data.data);
                if (data.data.length > 0 && !formId) {
                    setFormId(data.data[0].id);
                    setFormName(data.data[0].attributes.name);
                    setEventoSelecionado(normalizeEventId(data.data[0].attributes.event));
                    // Carrega configuração de quartos se existir
                    if (data.data[0].attributes.room_config) {
                        setRoomTypes(data.data[0].attributes.room_config);
                    }
                }

            } else {
                setError('Nenhum formulário encontrado.');
            }
        } catch (error) {
            console.error('Erro ao buscar formulários:', error);
            setError('Erro ao carregar formulários.');
        }
    };

    const handleUpdateForm = async () => {
        if (!formId || !formName ) {
            setError('Preencha o nome do hotel.');
            return;
        }
        
        // Valida se o total de leitos é suficiente para os hóspedes cadastrados
        if (totalLeitosDisponiveis < (totalHospedes || 0)) {
            setError(`Não é possível salvar! Total de leitos (${totalLeitosDisponiveis}) é menor que o total de hóspedes cadastrados (${totalHospedes}). Ajuste a configuração de quartos.`);
            return;
        }
        
        try {
            setLoading(true);
            await api.put(`/grids/${formId}`, {
                data: { 
                    name: formName,
                    event: normalizeEventId(eventoSelecionado) || null,
                    room_config: roomTypes // Salva configuração de quartos
                }
            });
            setSuccess('Formulário atualizado com sucesso!');
            fetchForms();
        } catch (error) {
            setError('Erro ao atualizar formulário: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleFormStatus = async () => {
        if (!formId) {
            setError('Nenhum formulário selecionado.');
            return;
        }
        
        try {
            setLoading(true);
            
            // Busca o status atual do formulário
            const currentForm = formOptions.find(f => f.id === formId);
            const currentStatus = currentForm?.attributes?.enabled ?? true;
            
            // Inverte o status
            await api.put(`/grids/${formId}`, {
                data: { 
                    enabled: !currentStatus
                }
            });
            
            const action = currentStatus ? 'desativado' : 'ativado';
            setSuccess(`Formulário ${action} com sucesso!`);
            
            // Atualiza a lista de formulários
            await fetchForms();
            
            // Se desativou o formulário atual, limpa a seleção
            if (currentStatus) {
                setFormId(null);
                setFormName('');
                setGuests([]);
            }
        } catch (error) {
            setError('Erro ao alterar status do formulário: ' + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveAll = async () => {
        // Valida se o total de leitos é suficiente para os hóspedes cadastrados
        if (totalLeitosDisponiveis < (totalHospedes || 0)) {
            setError(`Não é possível salvar! Total de leitos (${totalLeitosDisponiveis}) é menor que o total de hóspedes cadastrados (${totalHospedes}). Ajuste a configuração de quartos antes de salvar.`);
            return;
        }
        
        setLoading(true);
        try {
            const editedIndexes = guests
                .map((g, i) => g.edited ? i : null)
                .filter(i => i !== null);

            for (let i of editedIndexes) {
                await handleSave(i);
            }
            setSuccess('Todos os hóspedes foram salvos com sucesso!');
        } catch (error) {
            setError('Erro ao salvar todos os hóspedes: ' + error.message);
        } finally {
            setLoading(false);
        }
    };


    // Buscar hóspedes
    const fetchGuests = async () => {
        setLoading(true)
        console.log('Fetching guests for formId:', formId);
        if (!formId) return;
        let query = qs.stringify({
            filters: {
                grid: formId
            },
            sort: ['roomGroup:desc'],
            populate: {
                subscriber: {
                    fields: ['document', 'name', 'gender', 'email', 'birth', 'whatsapp'],
                },
            },
            pagination: {
                pageSize: pageSize,
                page: 1,
            },
            encodeValuesOnly: true,
        })
        try {
            const { data, status } = await api.get(`/guests?${query}`
            );

            if (status === 200) {
                setPageCount(data.meta.pagination.pageCount);
                setTotalHospedes(data.meta.pagination.total);
                // Mescla hóspedes do servidor com locais não salvos
                setGuests(prev => {
                    const serverGuests = data.data.map(guest => ({
                        id: guest.id,
                        grid: formId,
                        subscriber: guest.attributes.subscriber?.data?.id,
                        name: guest.attributes.subscriber?.data?.attributes?.name || '',
                        document: guest.attributes.subscriber?.data?.attributes?.document || '',
                        whatsapp: guest.attributes.subscriber?.data?.attributes?.whatsapp || '',
                        birth: guest.attributes.subscriber?.data?.attributes?.birth || '',
                        email: guest.attributes.subscriber?.data?.attributes?.email || '',
                        cidade: guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.city || '',
                        estado: guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.state || '',
                        gender: guest.attributes.subscriber?.data?.attributes?.gender || '',
                        checkin: guest.attributes.checkin || '',
                        checkout: guest.attributes.checkout || '',
                        type: guest.attributes.type || '',
                        roomGroup: guest.attributes.roomGroup || '',

                    }));
                    const counts = {};
                    roomTypes.forEach(rt => {
                        // Conta quantos hóspedes existem desse tipo
                        const totalGuests = serverGuests.filter(g => g.type === rt.label).length;
                        // Calcula quantos quartos estão ocupados (arredonda para cima)
                        const occupiedRooms = Math.ceil(totalGuests / rt.value);
                        // Badge mostra quartos disponíveis = total de quartos - quartos ocupados
                        counts[rt.label] = rt.rooms - occupiedRooms;
                    });
                    setBadgeCountByType(counts);
                    // Mantém os hóspedes locais que não foram salvos (sem ID)
                    const localUnsavedGuests = prev.filter(g => !g.id);
                    const mergedGuests = [...localUnsavedGuests, ...serverGuests];
                    applyDatesFromLastGuest(mergedGuests);

                    return mergedGuests;
                });

            }
        } catch (error) {
            console.error('Erro ao buscar hóspedes:', error);
            setError('Erro ao carregar hóspedes.');
        }
    };

    // Efeitos
    useEffect(() => {
        fetchForms();
    }, []);

    useEffect(() => {
        if (formId) {
            fetchGuests();
        }
    }, [formId]);

    // Criar novo formulário
    const handleCreateForm = async () => {
        try {
            const { data, status } = await api.post('/grids', {
                data: { 
                    name: formName, 
                    count_guests: Number(totalVagas), 
                    event: normalizeEventId(eventoSelecionado) || null,
                    enabled: true,
                    room_config: initialRoomTypes // Inicia com configuração padrão
                }
            });

            if (status === 200) {
                setFormId(data.data.id);
                setRoomTypes(initialRoomTypes); // Reseta configuração de quartos
                setSuccess(`Formulário "${formName}" criado!`);
                fetchForms();
            }
        } catch (error) {
            setError('Erro ao criar formulário: ' + error.message);
        }
    };

    // Função para atualizar o número de quartos de um tipo específico
    const handleRoomCountChange = (index, value) => {
        const newValue = Number(value) || 0;
        const newRoomTypes = [...roomTypes];
        newRoomTypes[index].rooms = newValue;
        
        // Calcula o total de leitos que seriam disponíveis com a nova configuração
        const newTotalLeitos = newRoomTypes.reduce((total, type) => {
            return total + (type.rooms * type.value);
        }, 0);
        
        // Verifica se excede o número de hóspedes já cadastrados
        if (newTotalLeitos < (totalHospedes || 0)) {
            setError(`Atenção! A configuração de ${newTotalLeitos} leitos é menor que o total de ${totalHospedes} hóspedes cadastrados. Ajuste antes de salvar.`);
        }
        
        setRoomTypes(newRoomTypes);
        
        // Recalcula os badges
        const counts = {};
        newRoomTypes.forEach(type => {
            const totalGuests = guests.filter(g => g.type === type.label).length;
            const occupiedRooms = Math.ceil(totalGuests / type.value);
            counts[type.label] = type.rooms - occupiedRooms;
        });
        setBadgeCountByType(counts);
    };

    // Buscar informações do CPF
    const handleCpfChange = async (idOrTempId, cpf) => {
        const idx = guests.findIndex(g => g.id === idOrTempId || g.tempId === idOrTempId);
        if (idx === -1) return;

        if (guests.some((g, i) => i !== idx && g.document === cpf)) {
            setError('CPF já cadastrado em outra linha!');
            return;
        }

        // Atualiza o CPF no estado local imediatamente
        setGuests(prev => prev.map((g, i) =>
            i === idx ? { ...g, document: cpf, edited: true } : g
        ));

        // Só prossegue se o CPF for válido e completo
        if (cpf.length === 14 && validateBr.cpf(cpf)) {
            try {
                setLoading(true);

                // 1. Busca informações do subscriber pelo CPF
                const { data: subscriberData } = await api.post('/subscriber/form', { data: { cpf } });

                // 2. Cria o objeto atualizado preservando o ID existente
                const updatedGuest = {
                    ...guests[idx], // Mantém todos os dados existentes (incluindo o ID se existir)
                    subscriber: subscriberData.id, // Define o subscriber ID
                    name: subscriberData.name || '', // Atualiza o nome
                    birth: subscriberData.birth || '', // Atualiza a data de nascimento
                    email: subscriberData.email || '', // Atualiza o email
                    whatsapp: subscriberData.whatsapp || '', // Atualiza o whatsapp 
                    gender: subscriberData.gender || '', // Atualiza o gênero
                    checkin,
                    checkout,
                    cidade: subscriberData.company.data?.attributes?.city || '', // Atualiza a cidade
                    estado: subscriberData.company.data?.attributes?.state || '', // Atualiza o estado
                    type: guests[idx].type || '', // Mantém o tipo de quarto
                    roomGroup: guests[idx].roomGroup || '', // Mantém o grupo de quarto
                    document: cpf // Garante que o CPF formatado seja mantido
                };

                setGuests(prev => prev.map((g, i) =>
                    i === idx ? updatedGuest : g
                ));

                // 3. Salva automaticamente no servidor
                let savedGuest;
                if (updatedGuest.id) {
                    // Se já existe, atualiza
                    const { data } = await api.put(`/guests/${updatedGuest.id}`, {
                        data: updatedGuest
                    });
                    savedGuest = data.data;
                } else {
                    // Se não existe, cria novo
                    const { data } = await api.post('/guests', {
                        data: updatedGuest
                    });
                    savedGuest = data.data;
                }

                // 4. Atualiza o estado com os dados completos do servidor
                setGuests(prev => prev.map((g, i) =>
                    i === idx ? {
                        ...updatedGuest,
                        id: savedGuest.id,
                        ...savedGuest.attributes
                    } : g
                ));

                setSuccess('Hóspede salvo automaticamente!');
            } catch (error) {
                setError('Erro ao salvar hóspede: ' + error.message);
            } finally {
                setLoading(false);
            }
        }
    }

    // Adicionar novos hóspedes
    const handleAddGuests = (e) => {
        e.preventDefault();

        if (!formId) {
            setError('Selecione um formulário.');
            return;
        }

        // Busca o objeto do tipo de quarto pela tab selecionada
        const typeObj = roomTypes.find(rt => rt.label === selectedTab);
        if (!typeObj) {
            setError('Tipo de quarto inválido.');
            return;
        }
        const roomTypeValue = typeObj.value;
        const prefixMap = {
            'Single': '100',
            'Duplo': '200',
            'Triplo': '300',
            'Quadruplo': '400',
            'Quintuplo': '500'
        };
        const prefix = prefixMap[selectedTab] || '';
        
        // Verifica limite de vagas com base na configuração de quartos
        if (vagasRestantes <= 0) {
            setError('Limite de vagas atingido! Configure mais quartos se necessário.');
            return;
        }

        // Calcula o próximo número do grupo para o tipo selecionado
        const groupNumbers = guests
            .filter(g => g.type === selectedTab && g.roomGroup?.startsWith(prefix))
            .map(g => {
                const match = g.roomGroup?.match(new RegExp(`^${prefix}(\\d+)$`));
                return match ? parseInt(match[1], 10) : 0;
            });
        const nextGroupNumber = groupNumbers.length > 0 ? Math.max(...groupNumbers) + 1 : 1;

        const quantidade = Math.min(Number(roomTypeValue), vagasRestantes);
        const newGuests = Array.from({ length: quantidade }, () => ({
            tempId: `${Date.now()}-${Math.random()}`,
            grid: formId,
            subscriber: null,
            type: selectedTab,
            roomGroup: `${prefix}${nextGroupNumber}`,
            document: '',
            name: '',
            gender: '',
            whatsapp: '',
            email: '',
            checkin,
            checkout,
            cidade: '',
            estado: '',
            birth: '',
            edited: true
        }));

        setGuests(prev => {
            const updatedGuests = [...newGuests, ...prev];
            
            // Recalcula os badges após adicionar novos hóspedes
            const counts = {};
            roomTypes.forEach(rt => {
                const totalGuests = updatedGuests.filter(g => g.type === rt.label).length;
                const occupiedRooms = Math.ceil(totalGuests / rt.value);
                counts[rt.label] = rt.rooms - occupiedRooms;
            });
            setBadgeCountByType(counts);
            
            return updatedGuests;
        });
    };

    // Editar campo na tabela
    const handleEditTable = (idOrTempId, key, value) => {
        setGuests(prev => prev.map(g =>
            (g.id === idOrTempId || g.tempId === idOrTempId)
                ? { ...g, [key]: value, edited: true }
                : g
        ));
    };
    // Salvar hóspede
    // Atualize a função handleSave para preservar os outros registros
    const handleSave = async (idOrTempId) => {
        // Valida se o total de leitos é suficiente para os hóspedes cadastrados
        if (totalLeitosDisponiveis < (totalHospedes || 0)) {
            setError(`Não é possível salvar! Total de leitos (${totalLeitosDisponiveis}) é menor que o total de hóspedes cadastrados (${totalHospedes}). Ajuste a configuração de quartos antes de salvar.`);
            return;
        }
        
        const idx = guests.findIndex(g => g.id === idOrTempId || g.tempId === idOrTempId);
        if (idx === -1) return;
        try {
            const guest = guests[idx];
            let updatedGuest = { ...guest };

            const subscriberPayload = {
                name: guest.name,
                document: guest.document,
                gender: guest.gender,
                whatsapp: guest.whatsapp,
                email: guest.email,
                birth: guest.birth
            };

            const { data } = await api.put(`/subscribers/${guest.subscriber}`, {
                data: subscriberPayload
            });

            updatedGuest = {
                ...updatedGuest,
                ...data,
                // Garante que o id do guest não seja sobrescrito
                id: updatedGuest.id
            };

            // 2. Se já tem ID, atualiza o hóspede, senão cria um novo
            if (guest.id) {
                const { data } = await api.put(`/guests/${guest.id}`, {
                    data: updatedGuest
                });
                updatedGuest = { ...updatedGuest, ...data.data.attributes };
            } else {
                const { data } = await api.post('/guests', {
                    data: updatedGuest
                });
                updatedGuest = {
                    ...updatedGuest,
                    id: data.data.id,
                    ...data.data.attributes
                };
            }

            // 3. Atualiza apenas o hóspede salvo, mantendo os outros
            setGuests(prev => prev.map((g, i) =>
                i === idx ? { ...updatedGuest, edited: false } : g
            ));

            setSuccess(`Hóspede ${guest.name} salvo com sucesso!`);
        } catch (error) {
            setError('Erro ao salvar hóspede: ' + error.message);
        }
    };

    const deleteGroupGuests = async (roomGroup) => {
        // Filtra todos os hóspedes do grupo
        const groupGuests = guests.filter(g => g.roomGroup === roomGroup);

        // Exclui do backend apenas os que já têm ID
        for (let guest of groupGuests) {
            if (guest.id) {
                try {
                    await api.delete(`/guests/${guest.id}`);
                } catch (error) {
                    setError('Erro ao excluir hóspede: ' + error.message);
                }
            }
        }

        // Remove todos do grupo do estado local e recalcula badges
        setGuests(prev => {
            const updatedGuests = prev.filter(g => g.roomGroup !== roomGroup);
            
            // Recalcula os badges após remover o grupo
            const counts = {};
            roomTypes.forEach(rt => {
                const totalGuests = updatedGuests.filter(g => g.type === rt.label).length;
                const occupiedRooms = Math.ceil(totalGuests / rt.value);
                counts[rt.label] = rt.rooms - occupiedRooms;
            });
            setBadgeCountByType(counts);
            
            return updatedGuests;
        });

        setDeleteDialogOpen(false);
        setDeleteGroup(null);
        setSuccess('Grupo excluído com sucesso!');
    };
    // Excluir hóspede
    const handleDelete = async (idOrTempId) => {
        const idx = guests.findIndex(g => g.id === idOrTempId || g.tempId === idOrTempId);
        if (idx === -1) return;
        const guest = guests[idx];
        if (guest.type !== 'Single') {
            setDeleteGroup({ roomGroup: guest.roomGroup, idx });
            setDeleteDialogOpen(true);
            return;
        }
        if (!guest.id) {
            // Se não foi salvo ainda, apenas remove da lista e recalcula badges
            setGuests(prev => {
                const updatedGuests = prev.filter((_, i) => i !== idx);
                
                // Recalcula os badges após remover o hóspede
                const counts = {};
                roomTypes.forEach(rt => {
                    const totalGuests = updatedGuests.filter(g => g.type === rt.label).length;
                    const occupiedRooms = Math.ceil(totalGuests / rt.value);
                    counts[rt.label] = rt.rooms - occupiedRooms;
                });
                setBadgeCountByType(counts);
                
                return updatedGuests;
            });
            return;
        }

        try {
            await api.delete(`/guests/${guest.id}`);
            setSuccess('Hóspede excluído com sucesso!');
            fetchGuests();
        } catch (error) {
            setError('Erro ao excluir hóspede: ' + error.message);
        }
    };

    // Exportar para Excel
    const handleExportExcel = async () => {
        try {
            // Busca os hóspedes atualizados da API
            const { data, status } = await api.get(
                `/guests?filters[grid]=${formId}` +
                `&filters[type]=${selectedTab}` +
                `&populate[subscriber][populate][company][fields][0]=city` +
                `&populate[subscriber][populate][company][fields][1]=state` +
                `&populate[subscriber][populate][company][fields][2]=trade` +
                `&populate[subscriber][populate][company][fields][3]=social_reason` +
                `&populate=*&pagination[page]=1` +
                `&pagination[pageSize]=1000`
            );

            if (status === 200) {
                const guestsToExport = data.data.map(guest => ({
                    "Check-in": guest.attributes.checkin ? format(parseISO(guest.attributes.checkin), "dd/MM/yyyy") : '',
                    "Check-out": guest.attributes.checkout ? format(parseISO(guest.attributes.checkout), "dd/MM/yyyy") : '',
                    //"Tipo de Quarto": guest.attributes.type || '',
                    "Tipo de Quarto": guest.attributes.roomGroup || '',
                    "CPF": guest.attributes.subscriber?.data?.attributes?.document || '',
                    "Nome": guest.attributes.subscriber?.data?.attributes?.name || '',
                    "Gênero": guest.attributes.subscriber?.data?.attributes?.gender === "M" ? "Masculino" : "Feminino",
                    "Nascimento": guest.attributes.subscriber?.data?.attributes?.birth
                        ? format(parseISO(guest.attributes.subscriber?.data?.attributes?.birth), "dd/MM/yyyy")
                        : '',
                    "Instituição": guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.trade || guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.social_reason || '',
                    "UF": guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.state || '',
                    "Cidade": guest.attributes.subscriber?.data?.attributes?.company?.data?.attributes?.city || '',
                    "Whatsapp": guest.attributes.subscriber?.data?.attributes?.whatsapp || '',
                    "Email": guest.attributes.subscriber?.data?.attributes?.email || '',

                }));

                const worksheet = XLSX.utils.json_to_sheet(guestsToExport);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Hóspedes");
                const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
                const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
                saveAs(blob, `hospedes-${formName}-${selectedTab}.xlsx`);
            }
        } catch (error) {
            setError('Erro ao exportar hóspedes: ' + error.message);
        }
    };
    const filteredGuests = useMemo(() => {
        let filtered = guests.filter(g => g.type === selectedTab);
        
        // Aplica filtro de pesquisa por nome
        if (searchName) {
            filtered = filtered.filter(g => 
                g.name?.toLowerCase().includes(searchName.toLowerCase())
            );
        }
        
        return filtered;
    }, [guests, selectedTab, searchName]);

    const handleTabChange = (event, newValue) => {
        setSelectedTab(newValue);
    };



    return (
        <Card sx={{ margin: 'auto', mt: 5, mx: 4, overflow: 'auto', width: isMobile ? '1280px' : '95%' }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Montar Grid de Hóspedes
                </Typography>

                <FormSelector
                    formName={formName}
                    formOptions={formOptions}
                    setFormName={setFormName}
                    setFormId={setFormId}
                    handleCreateForm={handleCreateForm}
                    handleUpdateForm={handleUpdateForm}
                    handleToggleFormStatus={handleToggleFormStatus}
                    totalVagas={totalVagas}
                    setTotalVagas={setTotalVagas}
                    eventoSelecionado={eventoSelecionado}
                    setEventoSelecionado={setEventoSelecionado}
                    events={events}
                    setRoomTypes={setRoomTypes}
                    initialRoomTypes={initialRoomTypes}
                />

                {/* Configuração de Número de Quartos por Tipo */}
                <Box sx={{ my: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Configuração de Quartos Disponíveis
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        {roomTypes.map((rt, index) => (
                            <Grid item xs={6} sm={2} key={rt.label}>
                                <TextField
                                    fullWidth
                                    label={`Quartos ${rt.label}`}
                                    type="number"
                                    value={rt.rooms}
                                    onChange={(e) => handleRoomCountChange(index, e.target.value)}
                                    inputProps={{ min: 0 }}
                                    size="small"
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                color="success"
                                fullWidth
                                onClick={handleUpdateForm}
                                disabled={!formId}
                            >
                                Salvar Config
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                <Box component="form" onSubmit={handleAddGuests} sx={{ my: 3 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item sm={1}>
                            <TextField
                                fullWidth
                                label="Total Leitos"
                                type="number"
                                disabled
                                value={totalLeitosDisponiveis}
                                InputProps={{ readOnly: true }}
                            />
                        </Grid>
                        <Grid item sm={2}>
                            <TextField
                                fullWidth
                                label="Check-in"
                                type="date"
                                value={checkin}
                                onChange={e => setCheckin(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item sm={2}>
                            <TextField
                                fullWidth
                                label="Check-out"
                                type="date"
                                value={checkout}
                                onChange={e => setCheckout(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                size="large"
                            >
                                Adicionar
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="success"
                                size="large"
                                onClick={handleExportExcel}
                                disabled={!guests.length}
                            >
                                Exportar para Excel
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                color="warning"
                                size="large"
                                onClick={handleSaveAll}
                                disabled={guests.length === 0}
                            >
                                Salvar Todos
                            </Button>
                        </Grid>
                        <Grid item xs={12}>
                            <Tabs
                                value={selectedTab}
                                onChange={handleTabChange}
                                fullWidth
                                selectionFollowsFocus
                                sx={{ mb: 2 }}
                            >
                                {roomTypes.map(rt => (
                                    <Tab
                                        key={rt.label}
                                        value={rt.label}
                                        label={<Badge
                                            overlap="circular"
                                            sx={{ p: 2 }}
                                            badgeContent={badgeCountByType[rt.label]}
                                            color="primary"
                                            showZero
                                            max={9999}
                                        >{rt.label}
                                        </Badge>
                                        }
                                    />
                                ))}
                            </Tabs>
                        </Grid>
                        
                        {/* Campo de pesquisa */}
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                label="Pesquisar por Nome"
                                variant="outlined"
                                value={searchName}
                                onChange={(e) => setSearchName(e.target.value)}
                                placeholder="Digite o nome do hóspede"
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </Box>

                <GuestsTable
                    guests={filteredGuests}
                    columns={columns}
                    handleEditTable={handleEditTable}
                    handleCpfChange={handleCpfChange}
                    handleSave={handleSave}
                    handleDelete={handleDelete}
                />

                <Stack direction="row" justifyContent="center" sx={{ my: 2 }}>
                    <Pagination
                        count={pageCount}
                        page={page}
                        onChange={(_, value) => {
                            setPage(value);
                            fetchGuests(value);
                        }}
                        color="primary"
                    />
                </Stack>

                <FeedbackMessage
                    open={!!error}
                    message={error}
                    severity="error"
                    onClose={() => setError('')}
                />
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle>Excluir grupo de hóspedes</DialogTitle>
                    <DialogContent>
                        Deseja excluir todos os hóspedes do grupo <b>{deleteGroup?.roomGroup}</b>?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => {
                                if (deleteGroup && deleteGroup.roomGroup) {
                                    deleteGroupGuests(deleteGroup.roomGroup);
                                }
                            }}
                            color="error"
                            variant="contained"
                        >
                            Excluir grupo
                        </Button>
                    </DialogActions>
                </Dialog>
                <FeedbackMessage
                    open={!!success}
                    message={success}
                    severity="success"
                    onClose={() => setSuccess('')}
                />
            </CardContent>
        </Card>
    );
};

export default GuestsPage;